import React from "react";

import Table from "@cloudscape-design/components/table";
import Pagination from "@cloudscape-design/components/pagination";
import Header from "@cloudscape-design/components/header";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import Preferences from "@cloudscape-design/components/collection-preferences";
import Alert from "@cloudscape-design/components/alert";
import SpaceBetween from "@cloudscape-design/components/space-between";
import FormField from "@cloudscape-design/components/form-field";
import Input from "@cloudscape-design/components/input";
import Grid from "@cloudscape-design/components/grid";
import Select from "@cloudscape-design/components/select";
import Multiselect from "@cloudscape-design/components/multiselect";
import DatePicker from "@cloudscape-design/components/date-picker";

const TYPE_OPTIONS = [
	{ value: "check", label: "Check" },
	{ value: "grant", label: "Grant" },
];

const CHECK_STATUS_OPTIONS = [
	{ value: "", label: "All results" },
	{ value: "success", label: "success" },
	{ value: "fail", label: "fail" },
];

const GRANT_STATUS_OPTIONS = [
	{ value: "", label: "All statuses" },
	{ value: "success", label: "success" },
	{ value: "failed", label: "failed" },
];

const PAGE_SIZE_OPTIONS = [
	{ value: 20, label: "20" },
	{ value: 50, label: "50" },
	{ value: 100, label: "100" },
];

const defaultFilters = () => ({
	type: "check",
	uid: "",
	reasonCodes: [],
	resultStatus: "",
	panelLogins: [],
	dateFrom: "",
	dateTo: "",
});

const formatDate = (value) => {
	if (value === null || typeof value === "undefined" || value === "" || value === 0) return "-";

	const stringValue = String(value).trim();
	if (stringValue === "0" || stringValue.indexOf("0000-00-00") === 0) return "-";

	let normalizedValue = value;
	if (/^\d+$/.test(stringValue)) {
		const timestamp = Number(stringValue);
		if (!Number.isFinite(timestamp) || timestamp <= 0) return "-";

		normalizedValue = timestamp < 100000000000 ? timestamp * 1000 : timestamp;
	}

	const date = new Date(normalizedValue);
	if (!Number.isFinite(date.getTime())) return String(value);

	return date.toLocaleDateString() + " " + date.toLocaleTimeString();
};

const isBlank = (value) => value === null || typeof value === "undefined" || value === "";

const displayValue = (value) => (isBlank(value) ? "-" : String(value));

const formatAmount = (amount, currency) => {
	if (isBlank(amount)) return "-";

	return [amount, currency].filter((value) => !isBlank(value)).join(" ");
};

const getStatusBadgeColor = (value) => {
	if (value === "success") return "green";
	if (value === "fail" || value === "failed") return "red";

	return "grey";
};

const STATUS_BADGE_BACKGROUNDS = {
	grey: "var(--color-background-notification-grey-ua4ep5, #414d5c)",
	green: "var(--color-background-notification-green-0d9ow8, #037f0c)",
	red: "var(--color-background-notification-red-v7js1f, #d91515)",
};

const STATUS_BADGE_STYLE = {
	backgroundColor: STATUS_BADGE_BACKGROUNDS.grey,
	borderRadius: "var(--border-radius-badge-x5oehc, 4px)",
	boxSizing: "border-box",
	color: "var(--color-text-notification-default-2kpcxy, #fbfbfb)",
	display: "inline-block",
	fontSize: "var(--font-body-s-size-ukw2p9, 12px)",
	letterSpacing: "var(--font-body-s-letter-spacing-cy0oxj, 0.005em)",
	lineHeight: "var(--font-body-m-line-height-i7xxvv, 22px)",
	padding: "0 var(--space-xs-rsr2qu, 8px)",
	textAlign: "center",
	width: "64px",
};

const getUniqueOptions = (items, field) => {
	const values = new Set();

	items.forEach((item) => {
		const value = item?.[field];
		if (isBlank(value)) return;

		values.add(String(value));
	});

	return Array.from(values)
		.sort((left, right) => left.localeCompare(right))
		.map((value) => ({ value, label: value }));
};

const mergeOptions = (currentOptions, nextOptions) => {
	const byValue = new Map();

	currentOptions.forEach((option) => byValue.set(option.value, option));
	nextOptions.forEach((option) => byValue.set(option.value, option));

	return Array.from(byValue.values()).sort((left, right) => left.label.localeCompare(right.label));
};

class VipFreespinsLogs extends React.Component {
	constructor(props) {
		super(props);

		const perPage = Number(localStorage.getItem("freespinsLogs.perPage")) || 20;

		this.state = {
			loaded: false,
			data: [],
			error: {
				text: "",
				code: 0,
				timestamp: 0,
			},
			filters: defaultFilters(),
			appliedFilters: defaultFilters(),
			pagination: {
				current: 1,
				total: 0,
				count: 0,
			},
			perPage,
			sorting: {
				column: "createdAt",
				order: "DESC",
				isDescending: true,
			},
			reasonOptions: [],
			reasonOptionsLoading: false,
			reasonOptionsError: "",
			employeeOptions: [],
		};
	}

	componentDidMount() {
		this.loadReasonOptions();
		this.loadLogs();
	}

	componentWillUnmount() {
		clearTimeout(this.filterApplyTimer);
	}

	getStatusOptions(type = this.state.filters.type) {
		return type === "grant" ? GRANT_STATUS_OPTIONS : CHECK_STATUS_OPTIONS;
	}

	getSelectedOption(options, value) {
		return options.find((option) => option.value === value) || null;
	}

	getSelectedOptions(options, values) {
		return (values || []).map((value) => options.find((option) => option.value === value) || { value, label: value });
	}

	getReasonOptions() {
		if (this.state.reasonOptions.length) return this.state.reasonOptions;

		return getUniqueOptions(this.state.data, "reasonCode");
	}

	getEmployeeOptions() {
		return this.state.employeeOptions;
	}

	getClientFilteredData() {
		const { data, appliedFilters } = this.state;
		const reasonCodes = new Set(appliedFilters.reasonCodes || []);
		const panelLogins = new Set(appliedFilters.panelLogins || []);

		if (!reasonCodes.size && !panelLogins.size) return data;

		return data.filter((item) => {
			if (reasonCodes.size && !reasonCodes.has(String(item.reasonCode))) return false;
			if (panelLogins.size && !panelLogins.has(String(item.panelLogin))) return false;

			return true;
		});
	}

	normalizeSelectedValues(values) {
		return (values || []).map((value) => String(value).trim()).filter((value) => value !== "");
	}

	buildRequestParams(filters, page, limit, sorting) {
		const offset = (page - 1) * limit;
		const params = {
			type: filters.type,
			page,
			limit,
			offset,
			sortBy: sorting.column,
			sortDirection: sorting.order,
		};

		if (filters.uid) params.uid = filters.uid;
		if ((filters.reasonCodes || []).length === 1) params.reasonCode = filters.reasonCodes[0];
		if ((filters.panelLogins || []).length === 1) params.panelLogin = filters.panelLogins[0];
		if (filters.dateFrom) params.dateFrom = `${filters.dateFrom} 00:00:00`;
		if (filters.dateTo) params.dateTo = `${filters.dateTo} 23:59:59`;

		if (filters.resultStatus) {
			if (filters.type === "grant") params.status = filters.resultStatus;
			else params.result = filters.resultStatus;
		}

		return params;
	}

	loadReasonOptions = () => {
		this.setState({
			reasonOptionsLoading: true,
			reasonOptionsError: "",
		});

		this.props.Interface.loadFreespinsRules({ limit: 1000 })
			.then((response) => {
				const rules = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
				const seen = new Set();
				const reasonOptions = rules
					.filter((rule) => !isBlank(rule.reasonCode))
					.reduce((options, rule) => {
						const value = String(rule.reasonCode);
						if (seen.has(value)) return options;

						seen.add(value);
						options.push({
							value,
							label: rule.reasonName ? `${rule.reasonName} (${value})` : value,
						});

						return options;
					}, [])
					.sort((left, right) => left.label.localeCompare(right.label));

				this.setState({
					reasonOptions,
					reasonOptionsLoading: false,
					reasonOptionsError: "",
				});
			})
			.catch((error) => {
				this.setState({
					reasonOptions: [],
					reasonOptionsLoading: false,
					reasonOptionsError: error?.text || error?.message || String(error),
				});
			});
	};

	loadLogs = (options = {}) => {
		const filters = options.filters || this.state.appliedFilters;
		const page = options.page || this.state.pagination.current;
		const limit = options.limit || this.state.perPage;
		const sorting = options.sorting || this.state.sorting;

		this.setState({
			loaded: false,
			error: {
				text: "",
				code: 0,
				timestamp: 0,
			},
		});

		this.props.Interface.loadFreespinsLogs(this.buildRequestParams(filters, page, limit, sorting))
			.then((response) => {
				const pagination = response.pagination || {};
				const data = response.data || [];

				this.setState({
					loaded: true,
					data,
					appliedFilters: filters,
					perPage: limit,
					sorting,
					employeeOptions: mergeOptions(this.state.employeeOptions, getUniqueOptions(data, "panelLogin")),
					pagination: {
						current: pagination.page || page,
						total: pagination.totalPages || 0,
						count: pagination.total || 0,
					},
					error: {
						text: "",
						code: 0,
						timestamp: 0,
					},
				});
			})
			.catch((error) => {
				this.setState({
					loaded: true,
					data: [],
					error,
					pagination: {
						...this.state.pagination,
						current: page,
						total: 0,
						count: 0,
					},
				});
			});
	};

	scheduleApplyFilters(delay = 0) {
		clearTimeout(this.filterApplyTimer);
		this.filterApplyTimer = setTimeout(() => {
			this.filterApplyTimer = null;
			this.applyFilters();
		}, delay);
	}

	updateFilter = (field, value) => {
		const applyDelay = field === "uid" ? 400 : 0;

		this.setState((state) => {
			const filters = {
				...state.filters,
				[field]: value,
			};

			if (field === "type") filters.resultStatus = "";

			return { filters };
		}, () => this.scheduleApplyFilters(applyDelay));
	};

	applyFilters = () => {
		const filters = {
			...this.state.filters,
			uid: this.state.filters.uid.trim(),
			reasonCodes: this.normalizeSelectedValues(this.state.filters.reasonCodes),
			panelLogins: this.normalizeSelectedValues(this.state.filters.panelLogins),
		};

		this.setState(
			{
				filters,
				appliedFilters: filters,
				pagination: {
					...this.state.pagination,
					current: 1,
				},
			},
			() => this.loadLogs({ filters, page: 1 })
		);
	};

	clearFilters = () => {
		clearTimeout(this.filterApplyTimer);

		const filters = defaultFilters();

		this.setState(
			{
				filters,
				appliedFilters: filters,
				pagination: {
					...this.state.pagination,
					current: 1,
				},
			},
			() => this.loadLogs({ filters, page: 1 })
		);
	};

	renderStatus(value) {
		const color = getStatusBadgeColor(value);

		return <span style={{ ...STATUS_BADGE_STYLE, backgroundColor: STATUS_BADGE_BACKGROUNDS[color] }}>{displayValue(value)}</span>;
	}

	getCheckColumns() {
		return [
			{ id: "createdAt", header: "Created", sortingField: "createdAt", cell: (item) => formatDate(item.createdAt) },
			{ id: "uid", header: "UID", sortingField: "uid", cell: (item) => displayValue(item.uid) },
			{ id: "panelLogin", header: "Employee", sortingField: "panelLogin", cell: (item) => displayValue(item.panelLogin) },
			{ id: "reasonCode", header: "Reason", sortingField: "reasonCode", cell: (item) => displayValue(item.reasonCode) },
			{ id: "result", header: "Result", cell: (item) => this.renderStatus(item.result) },
			{ id: "failedReason", header: "Failed reason", cell: (item) => displayValue(item.failedReason) },
		];
	}

	getGrantColumns() {
		return [
			{ id: "createdAt", header: "Created", sortingField: "createdAt", cell: (item) => formatDate(item.createdAt) },
			{ id: "uid", header: "UID", sortingField: "uid", cell: (item) => displayValue(item.uid) },
			{ id: "panelLogin", header: "Employee", sortingField: "panelLogin", cell: (item) => displayValue(item.panelLogin) },
			{ id: "reasonCode", header: "Reason", sortingField: "reasonCode", cell: (item) => displayValue(item.reasonCode) },
			{ id: "status", header: "Status", cell: (item) => this.renderStatus(item.status) },
			{ id: "gameId", header: "Game", cell: (item) => displayValue(item.gameId) },
			{ id: "providerId", header: "Provider", cell: (item) => displayValue(item.providerId) },
			{ id: "spinsCount", header: "Spins", cell: (item) => displayValue(item.spinsCount) },
			{ id: "betLevel", header: "Bet", cell: (item) => formatAmount(item.betLevel, item.currency) },
			{ id: "expiresAt", header: "Expires", cell: (item) => formatDate(item.expiresAt) },
		];
	}

	renderFilters() {
		const { filters, loaded } = this.state;
		const statusOptions = this.getStatusOptions(filters.type);
		const reasonOptions = this.getReasonOptions();
		const employeeOptions = this.getEmployeeOptions();

		return (
			<Box margin={{ bottom: "s" }}>
				<Grid gridDefinition={[{ colspan: 2 }, { colspan: 2 }, { colspan: 3 }, { colspan: 2 }, { colspan: 3 }]}>
					<FormField label="Type" stretch>
						<Select
							disabled={!loaded}
							selectedOption={this.getSelectedOption(TYPE_OPTIONS, filters.type)}
							onChange={({ detail }) => this.updateFilter("type", detail.selectedOption.value)}
							options={TYPE_OPTIONS}
						/>
					</FormField>
					<FormField label="UID" stretch>
						<Input
							disabled={!loaded}
							value={filters.uid}
							onChange={({ detail }) => this.updateFilter("uid", detail.value)}
							type="search"
						/>
					</FormField>
					<FormField label="Reason Code" stretch>
						<Multiselect
							disabled={!loaded || this.state.reasonOptionsLoading || (!reasonOptions.length && !filters.reasonCodes.length)}
							statusType={this.state.reasonOptionsLoading ? "loading" : "finished"}
							selectedOptions={this.getSelectedOptions(reasonOptions, filters.reasonCodes)}
							onChange={({ detail }) => this.updateFilter("reasonCodes", detail.selectedOptions.map((option) => option.value))}
							options={reasonOptions}
							placeholder={reasonOptions.length ? "All reason codes" : "No reason codes"}
							filteringType="auto"
							selectedAriaLabel="Selected"
							deselectAriaLabel={(option) => `Remove ${option.label}`}
							empty={this.state.reasonOptionsError ? "Rules unavailable. No loaded log reason codes." : "No reason codes available"}
							tokenLimit={1}
						/>
					</FormField>
					<FormField label={filters.type === "grant" ? "Status" : "Result"} stretch>
						<Select
							disabled={!loaded}
							selectedOption={this.getSelectedOption(statusOptions, filters.resultStatus)}
							onChange={({ detail }) => this.updateFilter("resultStatus", detail.selectedOption.value)}
							options={statusOptions}
						/>
					</FormField>
					<FormField label="Employee" stretch>
						<Multiselect
							disabled={!loaded || (!employeeOptions.length && !filters.panelLogins.length)}
							selectedOptions={this.getSelectedOptions(employeeOptions, filters.panelLogins)}
							onChange={({ detail }) => this.updateFilter("panelLogins", detail.selectedOptions.map((option) => option.value))}
							options={employeeOptions}
							placeholder={employeeOptions.length ? "All employees" : "No employees"}
							filteringType="auto"
							selectedAriaLabel="Selected"
							deselectAriaLabel={(option) => `Remove ${option.label}`}
							empty="No employees available"
							tokenLimit={1}
						/>
					</FormField>
				</Grid>
			</Box>
		);
	}

	renderHeaderActions() {
		const { filters, loaded } = this.state;

		return (
			<SpaceBetween direction="horizontal" size="xs">
				<FormField label={<span style={{ visibility: "hidden" }}>Actions</span>}>
					<Button disabled={!loaded} onClick={this.clearFilters}>
						Clear
					</Button>
				</FormField>
				<div style={{ width: "150px" }}>
					<FormField label="Date From" stretch>
						<DatePicker
							disabled={!loaded}
							value={filters.dateFrom}
							onChange={({ detail }) => this.updateFilter("dateFrom", detail.value)}
							placeholder="YYYY/MM/DD"
						/>
					</FormField>
				</div>
				<div style={{ width: "150px" }}>
					<FormField label="Date To" stretch>
						<DatePicker
							disabled={!loaded}
							value={filters.dateTo}
							onChange={({ detail }) => this.updateFilter("dateTo", detail.value)}
							placeholder="YYYY/MM/DD"
						/>
					</FormField>
				</div>
			</SpaceBetween>
		);
	}

	render() {
		const { loaded, error, appliedFilters, pagination, perPage, sorting } = this.state;
		const typeTitle = appliedFilters.type === "grant" ? "Grant logs" : "Check logs";
		const columns = appliedFilters.type === "grant" ? this.getGrantColumns() : this.getCheckColumns();
		const visibleData = this.getClientFilteredData();

		return (
			<React.Fragment>
				{loaded && (error.text || "").length ? (
					<Box margin={{ bottom: "m" }}>
						<Alert type="error" header="Error loading VIP FS logs" dismissible onDismiss={() => this.setState({ error: { text: "", code: 0, timestamp: 0 } })}>
							<Box variant="p">{error.text}</Box>
							<hr />
							<Box variant="p">
								Request error code: {error.code} | Request timestamp: {error.timestamp}
							</Box>
						</Alert>
					</Box>
				) : null}

				<Table
					stickyHeader
					trackBy="id"
					loading={!loaded}
					sortingColumn={{ sortingField: sorting.column }}
					sortingDescending={sorting.isDescending}
					onSortingChange={({ detail }) => {
						const nextSorting = {
							column: detail.sortingColumn.sortingField,
							order: detail.isDescending ? "DESC" : "ASC",
							isDescending: detail.isDescending,
						};

						this.setState({ sorting: nextSorting }, () => this.loadLogs({ sorting: nextSorting, page: 1 }));
					}}
					columnDefinitions={columns}
					items={visibleData}
					filter={this.renderFilters()}
					pagination={
						<Pagination
							disabled={!loaded}
							currentPageIndex={pagination.current}
							pagesCount={pagination.total}
							onChange={({ detail }) => {
								const page = detail.currentPageIndex;
								this.setState({ pagination: { ...pagination, current: page } }, () => this.loadLogs({ page }));
							}}
						/>
					}
					preferences={
						<Preferences
							cancelLabel="Cancel"
							confirmLabel="Confirm"
							pageSizePreference={{ title: "VIP FS logs per page", options: PAGE_SIZE_OPTIONS }}
							preferences={{ pageSize: perPage }}
							title="Preferences"
							onConfirm={({ detail }) => {
								localStorage.setItem("freespinsLogs.perPage", detail.pageSize);
								this.loadLogs({ page: 1, limit: Number(detail.pageSize) });
							}}
						/>
					}
					header={
						<Header
							variant="h2"
							description="Review VIP FreeSpins player checks and grant attempts"
							counter={`(${pagination.count})`}
							actions={this.renderHeaderActions()}
						>
							VIP FS Logs: {typeTitle}
						</Header>
					}
					empty={
						<Box textAlign="center" color="inherit">
							<b>No VIP FS logs found</b>
							<Box padding={{ bottom: "s" }} variant="p" color="inherit">
								Change filters or switch log type.
							</Box>
						</Box>
					}
				/>
			</React.Fragment>
		);
	}
}

export default VipFreespinsLogs;
